import { useState } from 'react';
import { IconInfo, IconSuccess } from '@divlab/divanui/icons';
import { cn, Popup, Price } from '@divlab/divanui';

import useMedias from '@Hooks/useMedias';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import { useRequest } from '@Contexts/Request';
import { useMeta } from '@Queries/Meta';
import images from './images';
import styles from './PromoCodePrices.module.css';

import type { FC, HTMLAttributes } from 'react';

type PromoCodePricesProps = HTMLAttributes<HTMLDivElement>;

const PromoCodePrices: FC<PromoCodePricesProps> = (props) => {
  const { className, ...restProps } = props;
  const { isMobile } = useMedias();
  const page = PageProductStore.useData();
  const {
    data: { currency },
  } = useMeta();
  const { isUzb } = useRequest();
  const { promoCode } = page;
  const expiredPrice = page.product.price.expired;
  const [visiblePopup, setVisiblePopup] = useState(false);

  const needToShowPopup = visiblePopup && !isMobile;

  const handleMouseEnter = () => {
    setVisiblePopup(true);
  };

  const handleMouseLeave = () => {
    setVisiblePopup(false);
  };

  const [isCodeCopied, setIsCodeCopied] = useState(false);

  const handleCopyPromoCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setIsCodeCopied(true);
    setTimeout(() => setIsCodeCopied(false), 5000);
  };

  const handleClick = () => {
    if (!isMobile) return;

    ModalWindows.open('PromoCodeInfo', {
      title: promoCode.popupTitle,
      info: promoCode.description,
    });
  };

  return (
    <div {...restProps} className={cn(styles.wrapper, className)}>
      <div className={styles.topContainer}>
        <div className={styles.info}>
          <div className={styles.description}>{promoCode.expiredDate}</div>
          {promoCode.description && (
            <>
              <IconInfo
                className={styles.icon}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
              />
              <Popup className={styles.infoPopup} visible={needToShowPopup}>
                {promoCode.description.map((text) => {
                  return (
                    <div
                      key={text}
                      className={styles.infoPopupText}
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  );
                })}
              </Popup>
            </>
          )}
        </div>
        <div className={styles.priceBlock}>
          <Price price={promoCode.discountPrice} className={styles.actualPrice} />
          <div className={styles.promocode} onClick={() => handleCopyPromoCode(promoCode.code)}>
            <div className={styles.code}>{promoCode.code}</div>
            {isCodeCopied ? (
              <IconSuccess className={styles.iconSuccess} />
            ) : (
              <img src={images.copy} className={styles.copyIcon} />
            )}
            <Popup className={styles.copiedPopup} visible={isCodeCopied}>
              <div className={styles.copiedPopupText}>{promoCode.successCopy}</div>
            </Popup>
          </div>
        </div>
      </div>
      <div className={styles.bottomContainer}>
        <div className={styles.description}>{promoCode.footerTitle}</div>
        <div
          className={styles.containerPrices}
          itemProp='offers'
          itemScope
          itemType='http://schema.org/Offer'
        >
          <meta itemProp='priceCurrency' content={currency} />

          <link itemProp='availability' href='http://schema.org/InStock' />

          <Price
            className={cn(styles.price, { [styles.longPrice]: isUzb })}
            price={page.product.price.actual}
            itemProp='price'
            content={page.product.price.actual.toString()}
          />
          <link itemProp='availability' href='http://schema.org/InStock' />
          {expiredPrice > 0 && (
            <Price
              expired
              className={cn(styles.expiredPrice, { [styles.longPrice]: isUzb })}
              price={expiredPrice}
              withoutCurrency={isUzb}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PromoCodePrices;
